/**
 * Recipe create/edit provider.
 *
 * Exposes collection queries plus recipe mutations. Platform UIs own ingredient/step editors and
 * only attach platform-specific success/error side effects.
 */
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  type PropsWithChildren,
  type ReactNode,
} from "react";
import { useQuery, useZero } from "@corpuscomplete/zero-data-transformer/react";
import type { MutatorResultErrorDetails } from "@rocicorp/zero";
import { createId } from "@corpuscomplete/core/utils";
import { mutators, queries } from "@corpuscomplete/core/zero";
import { DraftUI } from "../../../common/draft";
import { AppForm } from "../../../form";
import { FoodCollectionUI } from "../../food-collection";
import { Recipe } from "@corpuscomplete/core/nutrition/recipe";

export namespace RecipeForm {
  const CreateContext = createContext<ReturnType<typeof useCreateForm> | null>(null);
  type UseCreateFormInput = {
    collectionScopeId?: string;
    initialCollectionId?: string;
    onCreated?: (props: { recipeId: string }) => void | Promise<void>;
    onCreateError?: (error: MutatorResultErrorDetails["error"]) => void | Promise<void>;
  };

  function useCreateForm({
    collectionScopeId,
    initialCollectionId,
    onCreated,
    onCreateError,
  }: UseCreateFormInput = {}) {
    const z = useZero();
    const allManageableCollections = FoodCollectionUI.useForContentManagement();
    const manageableCollections = collectionScopeId
      ? allManageableCollections.filter((collection) => collection.scopeId === collectionScopeId)
      : allManageableCollections;
    const resolvedCollectionId = FoodCollectionUI.resolveManagedCollectionId({
      requestedCollectionId: initialCollectionId,
      collections: manageableCollections,
      foodType: "recipe",
    });

    return DraftUI.useDraftForm({
      type: "recipe.create",
      schemas: {
        storage: Recipe.formSchema("storage"),
        validation: Recipe.formSchema("validation"),
      },
      defaultValue: () => Recipe.formDefault(resolvedCollectionId),
      onSubmit: async ({ value }) => {
        const recipeId = createId();

        const result = z.mutate(mutators.recipe.create(Recipe.formToCreate(value, recipeId)));
        const serverResult = await result.server;

        if (serverResult.type === "success") {
          await onCreated?.({ recipeId });
        } else {
          await onCreateError?.(serverResult.error);
        }
      },
    });
  }

  export function CreateProvider({
    children,
    collectionScopeId,
    initialCollectionId,
    onCreated,
    onCreateError,
  }: PropsWithChildren<UseCreateFormInput>) {
    const value = useCreateForm({
      collectionScopeId,
      initialCollectionId,
      onCreated,
      onCreateError,
    });
    return <CreateContext.Provider value={value}>{children}</CreateContext.Provider>;
  }

  export function useCreate() {
    const value = useContext(CreateContext);
    if (!value) {
      throw new Error("RecipeForm.useCreate() must be used within RecipeForm.CreateProvider");
    }
    return value;
  }

  const EditContext = createContext<ReturnType<typeof useEditForm> | null>(null);

  type UseEditFormInput = {
    recipe: Recipe.Info;
    onSaved?: () => void | Promise<void>;
    onSaveError?: (error: MutatorResultErrorDetails["error"]) => void | Promise<void>;
  };

  function useEditForm({ recipe, onSaved, onSaveError }: UseEditFormInput) {
    const z = useZero();
    const defaultValues = useMemo(() => Recipe.infoToForm(recipe), [recipe]);

    return AppForm.useAppForm({
      formId: `recipe.edit.${recipe.id}`,
      defaultValues,
      validators: { onSubmit: Recipe.formSchema("validation") },
      onSubmit: async ({ value }) => {
        const result = z.mutate(mutators.recipe.edit(Recipe.formToEdit(value, recipe)));
        const serverResult = await result.server;

        if (serverResult.type === "success") {
          await onSaved?.();
        } else {
          await onSaveError?.(serverResult.error);
        }
      },
    });
  }

  export function EditProvider({
    recipeId,
    children,
    fallback,
    onNotFound,
    onSaved,
    onSaveError,
  }: PropsWithChildren<{
    recipeId: string;
    fallback?: ReactNode;
    onNotFound?: () => void | Promise<void>;
    onSaved?: () => void | Promise<void>;
    onSaveError?: (error: MutatorResultErrorDetails["error"]) => void | Promise<void>;
  }>) {
    const [rawRecipe, details] = useQuery(queries.recipe.byId({ id: recipeId }));
    const collections = FoodCollectionUI.useForContents();
    const { loading: collectionsLoading } = FoodCollectionUI.useLoading();
    const recipe = useMemo(() => (rawRecipe ? Recipe.asInfo(rawRecipe) : undefined), [rawRecipe]);
    const recipeCollection = recipe
      ? collections.find((collection) => collection.id === recipe.meta.collectionId)
      : undefined;

    if (details.type === "unknown" || collectionsLoading) {
      return <>{fallback ?? null}</>;
    }

    if (!recipe || !recipeCollection) {
      return <EditProviderNotFound onNotFound={onNotFound} />;
    }

    return (
      <EditProviderContent recipe={recipe} onSaved={onSaved} onSaveError={onSaveError}>
        {children}
      </EditProviderContent>
    );
  }

  function EditProviderNotFound({ onNotFound }: { onNotFound?: () => void | Promise<void> }) {
    useEffect(() => {
      void onNotFound?.();
    }, [onNotFound]);

    return null;
  }

  function EditProviderContent({
    recipe,
    children,
    onSaved,
    onSaveError,
  }: PropsWithChildren<UseEditFormInput>) {
    const value = useEditForm({ recipe, onSaved, onSaveError });
    return <EditContext.Provider value={value}>{children}</EditContext.Provider>;
  }

  export function useEdit() {
    const value = useContext(EditContext);
    if (!value) {
      throw new Error("RecipeForm.useEdit() must be used within RecipeForm.EditProvider");
    }
    return value;
  }
}
